import {
  type DataColumn,
  type DataTableModel,
  cellDisplay,
  isEmptyCell,
  numericValue,
  tableToTsv
} from '@/lib/dataTable';
import { cn } from '@/lib/utils';
import { buildXlsx, downloadXlsx } from '@/lib/xlsx';
import {
  ArrowDown,
  ArrowDown01,
  ArrowUp,
  Check,
  Copy,
  Download
} from 'lucide-react';
import { useMemo, useState } from 'react';

import type { IMessageElement } from '@chainlit/react-client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

/** More rows than this and the table scrolls in its own box. */
export const TALL_TABLE_ROWS = 12;
const TALL_TABLE_MAX_HEIGHT = 'min(65vh, 560px)';

/**
 * The data table in a chat answer.
 *
 * Replaces the generic markdown table, whose 16px cell padding and left
 * alignment wrapped `502.8M` onto two lines and rendered an Arabic table LTR.
 *
 * Three things this does that the generic table could not:
 *
 *  - Direction comes from the table's own content, not a global setting. One
 *    thread can hold an Arabic answer and an English one; the label column pins
 *    to whichever edge that table is read from.
 *  - Figures are tabular, right-of-the-label, and each one is its own `dir=ltr`
 *    run, so a minus sign or a decimal point cannot drift to the wrong end
 *    inside an RTL paragraph.
 *  - Sorting is by magnitude: `3.15B` sorts above `502.8M`.
 */

type SortDirection = 'asc' | 'desc';

interface Props {
  model: DataTableModel;
  caption?: string;
  /** The whole table, when the chat shows only part of it. */
  fullTableFile?: IMessageElement;
}

const numberCellStyle: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontVariantNumeric: 'tabular-nums'
};

export const DataTable = ({ model, caption, fullTableFile }: Props) => {
  const [sort, setSort] = useState<{
    index: number;
    direction: SortDirection;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const rtl = model.dir === 'rtl';
  const { columns, totals } = model;
  // A long table scrolls inside its own box, so the header stays in view while
  // the rows move under it (it was already position:sticky, but with no height
  // limit the box never scrolled -- the page did, and the header left with it).
  // Short tables keep overflow-y hidden: see the note on the wrapper below.
  const tall = model.rows.length + (totals ? 1 : 0) > TALL_TABLE_ROWS;

  const rows = useMemo(() => {
    if (!sort) return model.rows;
    const { index, direction } = sort;
    const factor = direction === 'asc' ? 1 : -1;
    // Slice first: sorting in place would mutate the parsed model and make the
    // next render start from the previous order.
    return [...model.rows].sort((a, b) => {
      const av = a[index] ?? '';
      const bv = b[index] ?? '';
      // Blanks sink to the bottom in either direction — a row with no value is
      // not "the smallest", it is unknown, and floating it to the top of a
      // descending sort would read as a real result.
      const aEmpty = isEmptyCell(av);
      const bEmpty = isEmptyCell(bv);
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      const an = numericValue(av);
      const bn = numericValue(bv);
      if (an !== null && bn !== null) return (an - bn) * factor;
      return av.localeCompare(bv, rtl ? 'ar' : undefined) * factor;
    });
  }, [model.rows, sort, rtl]);

  const toggleSort = (index: number) => {
    setSort((prev) =>
      prev?.index === index
        ? { index, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : // Figures are most useful largest-first; names read A-Z.
          {
            index,
            direction: columns[index]?.kind === 'label' ? 'asc' : 'desc'
          }
    );
  };

  const copyTsv = async () => {
    const tsv = tableToTsv({ ...model, rows });
    try {
      await navigator.clipboard.writeText(tsv);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access is blocked outside a secure context; a silent no-op
      // would look like the button is broken.
      window.prompt('Copy the table:', tsv);
    }
  };

  const downloadExcel = () => {
    // The chat shows 200 rows; the answer may have 17,000. When the whole
    // table came with the answer, THAT is what Excel means -- the rows on
    // screen are only what fits in a chat bubble.
    if (fullTableFile?.url) {
      const link = document.createElement('a');
      link.href = fullTableFile.url;
      link.download = fullTableFile.name || 'signals.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }
    // A real workbook, not a CSV. Figures are written as numbers so they sum,
    // sort and chart in Excel; a CSV handed over `3.15B` as text and the first
    // thing the user did was retype the column.
    const header = columns.map((c) =>
      c.currency ? `${c.header} (${c.currency})` : c.header
    );
    const body = [...rows, ...(totals ? [totals] : [])].map((row) =>
      columns.map((c, i) => {
        const raw = row[i] ?? '';
        const numeric =
          c.kind === 'number' || c.kind === 'percent'
            ? numericValue(raw)
            : null;
        // A percentage is written as the number the answer showed (68.3), not
        // as a fraction: the column header already says it is a percentage.
        return { text: cellDisplay(raw, c), value: numeric };
      })
    );
    const name =
      (caption || 'signals-table')
        .replace(/[\\/:*?"<>|]+/g, '')
        .trim()
        .slice(0, 60) || 'signals-table';
    downloadXlsx(buildXlsx(header, body, caption || 'Signals'), `${name}.xlsx`);
  };

  // The label column pins to the reading edge, so it stays visible while the
  // figures scroll under it.
  const stickyEdge = (header: boolean): React.CSSProperties => ({
    position: 'sticky',
    [rtl ? 'right' : 'left']: 0,
    zIndex: header ? 3 : 1,
    background: header ? 'var(--sig-table-head)' : 'var(--sig-surface)',
    [rtl ? 'borderLeft' : 'borderRight']: '1px solid var(--sig-border-light)'
  });

  const percentMax = useMemo(() => {
    // Bars are read against each other, so scale to the column's own top value
    // when something exceeds 100% rather than clipping every bar at full.
    const maxima: Record<number, number> = {};
    columns.forEach((c, i) => {
      if (c.kind !== 'percent') return;
      const values = model.rows
        .map((r) => numericValue(r[i] ?? ''))
        .filter((v): v is number => v !== null);
      maxima[i] = Math.max(100, ...values.map((v) => Math.abs(v)));
    });
    return maxima;
  }, [columns, model.rows]);

  const renderCell = (
    value: string,
    column: DataColumn,
    columnIndex: number,
    variant: 'body' | 'totals'
  ) => {
    const isTotals = variant === 'totals';
    const isLabel = columnIndex === 0;
    const display = cellDisplay(value, column);
    const numeric = column.kind === 'number' || column.kind === 'percent';

    const base: React.CSSProperties = {
      padding: '10px 12px',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      borderBottom: isTotals ? 'none' : '1px solid var(--sig-border-row)',
      ...(isTotals
        ? { background: 'var(--sig-table-head)', fontWeight: 700 }
        : null),
      ...(isLabel
        ? {
            ...stickyEdge(false),
            ...(isTotals ? { background: 'var(--sig-table-head)' } : null),
            textAlign: rtl ? 'right' : 'left',
            fontWeight: isTotals ? 700 : 600,
            color: 'var(--sig-ink)',
            fontFamily: 'Inter, sans-serif',
            ...(isTotals && tall ? { zIndex: 3 } : null)
          }
        : {
            textAlign: rtl ? 'left' : 'right',
            color: isTotals
              ? 'var(--sig-ink)'
              : column.emphasised
                ? 'var(--sig-ink)'
                : 'var(--sig-body)',
            ...(numeric ? numberCellStyle : null),
            ...(column.emphasised && !isTotals
              ? { fontWeight: 600, background: '#fafcfd' }
              : null),
            ...(isTotals && tall ? { position: 'sticky', zIndex: 2 } : null)
          }),
      // The totals row pins to the bottom of a scrolling table, the same way
      // the header pins to the top.
      ...(isTotals && tall
        ? {
            bottom: 0,
            borderTop: '1px solid var(--sig-border-strong)'
          }
        : null)
    };

    if (column.kind === 'percent' && !isTotals) {
      const v = numericValue(value);
      const width =
        v === null
          ? 0
          : Math.max(
              0,
              Math.min(
                100,
                (Math.abs(v) / (percentMax[columnIndex] || 100)) * 100
              )
            );
      return (
        <td key={columnIndex} style={base}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: rtl ? 'flex-start' : 'flex-end',
              gap: 8,
              flexDirection: rtl ? 'row-reverse' : 'row'
            }}
          >
            <div
              style={{
                width: 52,
                height: 5,
                borderRadius: 3,
                background: 'var(--sig-border)',
                overflow: 'hidden',
                flex: 'none'
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${width}%`,
                  borderRadius: 3,
                  background:
                    v !== null && v < 0
                      ? 'var(--sig-negative)'
                      : 'var(--sig-blue)'
                }}
              />
            </div>
            <span
              dir="ltr"
              style={{
                ...numberCellStyle,
                color: 'var(--sig-ink)',
                fontWeight: 600,
                minWidth: 44,
                textAlign: 'right'
              }}
            >
              {display}
            </span>
          </div>
        </td>
      );
    }

    return (
      <td
        key={columnIndex}
        style={base}
        // A bare figure inside an RTL row would take the row's direction and
        // put its sign, decimal point and unit at the wrong end.
        dir={numeric ? 'ltr' : 'auto'}
      >
        {display}
      </td>
    );
  };

  const shape = `${model.rows.length} ${
    model.rows.length === 1 ? 'row' : 'rows'
  } · ${columns.length} ${columns.length === 1 ? 'column' : 'columns'}`;

  const toolbarButton =
    'sig-toolbar-btn flex items-center gap-[5px] text-[11.5px] font-medium rounded-[7px] px-[9px] py-[5px] whitespace-nowrap transition-colors';

  return (
    <div
      className="[&:not(:first-child)]:mt-2 [&:not(:last-child)]:mb-2"
      style={{
        border: '1px solid var(--sig-border)',
        borderRadius: 12,
        overflow: 'hidden',
        background: 'var(--sig-surface)'
      }}
    >
      <div
        className="sig-toolbar flex items-center justify-between gap-3"
        style={{
          padding: '9px 12px',
          borderBottom: '1px solid var(--sig-border-light)',
          background: 'var(--sig-panel)'
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {caption ? (
            <span
              className="text-[13px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
              style={{ color: 'var(--sig-ink)' }}
              dir="auto"
            >
              {caption}
            </span>
          ) : null}
          <span
            className="text-[11.5px] whitespace-nowrap"
            style={{
              color: 'var(--sig-subtle)',
              fontFamily: 'Inter, sans-serif'
            }}
            dir="ltr"
          >
            {shape}
          </span>
        </div>

        <div
          className="flex items-center gap-1"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {/* The same sort a header click gives, reachable on touch. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={toolbarButton}
                style={{
                  border: '1px solid var(--sig-border-strong)',
                  background: 'var(--sig-surface)',
                  color: 'var(--sig-body)'
                }}
              >
                <ArrowDown01 size={12} strokeWidth={2.2} />
                Sort
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {columns.map((c, i) => (
                <DropdownMenuItem
                  key={`${c.header}-${i}`}
                  onSelect={() => toggleSort(i)}
                  className="text-[12.5px] gap-2"
                >
                  <span dir="auto" className="flex-1 truncate">
                    {c.header || `Column ${i + 1}`}
                  </span>
                  {sort?.index === i ? (
                    sort.direction === 'asc' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : null}
                </DropdownMenuItem>
              ))}
              {sort ? (
                <DropdownMenuItem
                  onSelect={() => setSort(null)}
                  className="text-[12.5px]"
                  style={{ color: 'var(--sig-muted)' }}
                >
                  Original order
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            onClick={copyTsv}
            className={toolbarButton}
            style={{
              border: '1px solid var(--sig-border-strong)',
              background: 'var(--sig-surface)',
              color: 'var(--sig-body)'
            }}
          >
            {copied ? (
              <Check size={12} strokeWidth={2.2} />
            ) : (
              <Copy size={12} strokeWidth={2.2} />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>

          <button
            type="button"
            onClick={downloadExcel}
            className={cn(toolbarButton, 'font-semibold')}
            style={{
              background: 'var(--sig-blue)',
              color: '#fff',
              padding: '5px 10px'
            }}
          >
            <Download size={12} strokeWidth={2.2} />
            Excel
          </button>
        </div>
      </div>

      {/* overflow-y must be set explicitly. With only overflow-x:auto, CSS
          computes overflow-y to auto as well, the wrapper sizes to
          "table height + scrollbar height", and a classic 15px scrollbar
          (Windows Chrome — most of our users) clips the totals row in half and
          raises a phantom vertical scrollbar. */}
      <div
        className="sig-table-scroll"
        style={{
          overflowX: 'auto',
          overflowY: tall ? 'auto' : 'hidden',
          maxHeight: tall ? TALL_TABLE_MAX_HEIGHT : undefined,
          scrollbarGutter: 'stable'
        }}
      >
        <table
          dir={model.dir}
          style={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: '100%',
            // The design says 720px, which is what this particular 7-column
            // table needs. Deriving it from the content instead means a
            // two-column answer does not force a horizontal scroll it never
            // needed — which is most of what a phone sees. Every cell is
            // nowrap, so max-content is exactly the width the table requires.
            minWidth: 'max-content',
            fontSize: 13
          }}
        >
          <thead>
            <tr>
              {columns.map((c, i) => {
                const active = sort?.index === i;
                const isLabel = i === 0;
                return (
                  <th
                    key={`${c.header}-${i}`}
                    onClick={() => toggleSort(i)}
                    title="Sort by this column"
                    style={{
                      position: 'sticky',
                      top: 0,
                      background: 'var(--sig-table-head)',
                      padding: '9px 12px',
                      fontSize: 11.5,
                      fontWeight: c.emphasised ? 700 : 600,
                      color: c.emphasised
                        ? 'var(--sig-ink)'
                        : 'var(--sig-body)',
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid var(--sig-border-strong)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      textAlign: isLabel
                        ? rtl
                          ? 'right'
                          : 'left'
                        : rtl
                          ? 'left'
                          : 'right',
                      ...(isLabel ? stickyEdge(true) : { zIndex: 2 })
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        flexDirection: rtl && !isLabel ? 'row-reverse' : 'row'
                      }}
                    >
                      <span dir="auto">{c.header}</span>
                      {c.currency ? (
                        <span
                          dir="ltr"
                          style={{
                            color: 'var(--sig-subtle)',
                            fontWeight: 500
                          }}
                        >
                          {c.currency}
                        </span>
                      ) : null}
                      {active ? (
                        sort!.direction === 'asc' ? (
                          <ArrowUp
                            size={11}
                            style={{ color: 'var(--sig-blue)' }}
                          />
                        ) : (
                          <ArrowDown
                            size={11}
                            style={{ color: 'var(--sig-blue)' }}
                          />
                        )
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr
                key={r}
                className="sig-row"
                style={{ transition: 'background-color .12s' }}
              >
                {columns.map((c, i) => renderCell(row[i] ?? '', c, i, 'body'))}
              </tr>
            ))}
            {totals ? (
              <tr>
                {columns.map((c, i) =>
                  renderCell(totals[i] ?? '', c, i, 'totals')
                )}
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};
